import type { Option, Question, ResultQuestion, SurveyContent, SurveyResult, SurveySubmission } from './contract'
import { decideApiBase } from './api-origin'
import { ApiError, httpGet, httpPost } from './http'
import { isOptionQuestion, isQuestionType } from './question-type'

// Hand-written runtime shape checks (no Zod). Only the fields render actually reads are validated,
// so a malformed payload is rejected at the API boundary rather than deep in a template.
// 「有哪些題型」與「哪些題型需要 options」都向 question-type.ts 要，不在這裡再列一次。

// thumbnail 直接綁進 <img src>。正式（live）路徑只收 https，其餘一律當作缺圖，避免任意 URL 變成
// 讀者 IP 的 tracking pixel。
//
// preview 另外收 raster 的 data:image（jpeg／png／webp／gif，base64）：使用者選了封面、還沒儲存時，
// admin 餵進來的是本地圖片轉成的 data URL。data: 圖片是自含內容、不發任何網路請求，不構成 tracking
// pixel；資料來源也是 admin 自己，不是任意第三方。這一條只在呼叫端明示 allowDataImageThumbnail 時
// 生效（見 parseSurvey），所以 live 路徑的行為不變。svg 與其他 data:、blob:、http: 兩條路徑都擋。
function isHttpsUrl(x: unknown): x is string {
  if (typeof x !== 'string')
    return false
  try {
    return new URL(x).protocol === 'https:'
  }
  catch {
    return false
  }
}

const RASTER_DATA_IMAGE_PREFIX = /^data:image\/(?:jpeg|png|webp|gif);base64,/

function isAcceptedThumbnail(x: unknown, opts: ParseSurveyOptions): x is string {
  if (isHttpsUrl(x))
    return true
  return opts.allowDataImageThumbnail === true && typeof x === 'string' && RASTER_DATA_IMAGE_PREFIX.test(x)
}

// Absolute base URL — the widget runs on TVBS IP host pages. Default baked at build time
// (VITE_API_BASE_URL). Version prefix /v1 lives at the call site; base is origin only.
//
// 這支只做一件事：把三個全域讀進來交給 decideApiBase，再把「被拒絕」這件事說出去。政策本身
// （allowlist、DEV 放寬、非字串 override）在 api-origin.ts，不必經過網路就驗得到。
//
// Resolved per request (not at module load): the SDK's cmd queue exists precisely because
// script load order isn't controllable, so apiBase must not demand the host set it "before the
// script loads". Under the trusted-integrator model (host = first-party TVBS IP site, D3=A), a
// host setting window.Pulse.apiBase is a legitimate feature. The allowlist is a
// misconfiguration / partial-compromise guard — a stray or compromised earlier-running script
// can't redirect reader MPID + answers to an arbitrary host — NOT a malicious-host sandbox
// (Shadow DOM is not a JS sandbox; a truly hostile host page is out of scope by D3).
function resolveBaseUrl(): string {
  const decision = decideApiBase({
    baked: import.meta.env.VITE_API_BASE_URL,
    override: (globalThis as { Pulse?: { apiBase?: string } }).Pulse?.apiBase,
    isDev: import.meta.env.DEV,
  })
  // 說出來的時機屬於這裡：政策是純的，warn 是副作用。
  // 用 String() 而不是把 override 插進樣板：override 是宿主注入的任意值，而樣板對 Symbol 會
  // 直接 throw（`String(Symbol('x'))` 回 'Symbol(x)'，`${Symbol('x')}` 拋 TypeError）——在這裡
  // throw 會讓一個「被忽略的設定」升級成整支載入失敗。
  if (decision.outcome === 'rejected')
    console.warn(`[Pulse] ignoring apiBase override outside the allowlist: ${String(decision.override)}`)
  return decision.base
}

// 這兩支寫成型別述詞而不是回傳 boolean，讓 `parseSurvey` 建得出一個真的標成 SurveyContent 的
// 物件，不必用 cast 宣稱。**TypeScript 不會驗證述詞的本體**——它是作者的斷言，不是證明，所以
// 「這些欄位真的被驗了」由 contract.test.ts 逐個必填欄位餵壞資料釘住。
//
// ⚠️ 述詞斷言的是必填欄位，加上「帶了就要是對的」的 options。其餘選填欄位（Option 的 is_other，
// Question 的 category / is_two_column / selection_limit / placeholder）的型別沒有驗：它們在
// runtime 原樣透傳，缺值時各有 widget 自己的預設行為（例如 is_two_column 缺值就是單欄）。
// 完整的選填欄位清單看 contract.ts 裡 Option 與 Question 帶 `?` 的那些，扣掉有驗的 options，
// 剩下的就是未驗的範圍。
function isValidOption(x: unknown): x is Option {
  if (typeof x !== 'object' || x === null)
    return false
  const o = x as Record<string, unknown>
  return typeof o.seq === 'number' && typeof o.content === 'string'
}

function isValidQuestion(x: unknown): x is Question {
  if (typeof x !== 'object' || x === null)
    return false
  const o = x as Record<string, unknown>
  if (typeof o.id !== 'string' || !isQuestionType(o.type))
    return false
  // render 讀 text（顯示）、submit 用 seq/is_required；非預期型別的題視為壞題，過濾掉。
  if (typeof o.text !== 'string' || typeof o.seq !== 'number' || typeof o.is_required !== 'boolean')
    return false
  // 帶了 options 就得是合法的選項陣列 —— 不分題型。自由填答題本來就不該有 options，但
  // Question.options 的型別是 Option[]，放行一個字串等於讓那個型別說謊，而讀它的地方不是每一處
  // 都先問過題型（`rg '\.options' src/` 找得到；prefill.ts 那處的守門是「存下來的答案是不是陣列」，
  // 不是題型）。
  if (o.options !== undefined && !(Array.isArray(o.options) && o.options.every(isValidOption)))
    return false
  // 選項題要渲染每一個選項，所以 options 對它們是必要的；自由填答題沒有也正常。
  return !isOptionQuestion(o.type) || Array.isArray(o.options)
}

/** 一份問卷裡沒通過驗證、因而不會被渲染的題目。 */
export interface DroppedQuestions {
  total: number
  // 其中必填的題數。這是嚴重度的分野：濾掉一題選填的，讀者少看到一題；濾掉一題必填的，讀者
  // 根本沒被問到，送出的 payload 就少那一筆，後端收到一份「合法但不完整」的回應。
  required: number
}

export interface ParsedSurvey {
  survey: SurveyContent
  dropped: DroppedQuestions
}

export interface ParseSurveyOptions {
  // 只有 preview 的呼叫端傳 true：thumbnail 除了 https，也收 raster 的 data:image（理由見上方
  // isHttpsUrl 的註解）。預設與缺值都是 false，正式路徑不傳。
  allowDataImageThumbnail?: boolean
}

// 過濾的結果會被說出來，但說話的時機屬於呼叫端（見 warnMalformedSurvey / warnDroppedQuestions）。
// 這支保持純函式：同一份資料在 preview 會被反覆解析（admin 每改一個字就重新套用一次），
// 在這裡 warn 等於照著 keystroke 洗 console。
//
// Tolerant reader：過濾掉未知/壞題型，而非整份拒收。widget 部署在第三方 CDN、更新最慢，
// 後端新增一種題型時舊版 embed 不該整份炸錯誤卡。top-level 結構壞才回 null（真 malformed）；
// 過濾後題目數為 0 由呼叫端當「沒有可投放的問卷」收合處理，不是錯誤。
export function parseSurvey(x: unknown, opts: ParseSurveyOptions = {}): ParsedSurvey | null {
  if (typeof x !== 'object' || x === null)
    return null
  const o = x as Record<string, unknown>
  if (typeof o.id !== 'string' || typeof o.title !== 'string' || !Array.isArray(o.questions))
    return null
  const questions = o.questions.filter(isValidQuestion)
  const rejects = o.questions.filter(q => !isValidQuestion(q))
  const dropped: DroppedQuestions = {
    total: rejects.length,
    required: rejects.filter(q => (q as Record<string, unknown> | null)?.is_required === true).length,
  }
  // 統計期間是選填的：非字串一律當作沒送，否則數字之類的值會被 new Date() 當成 timestamp
  // 解析成功，渲染出一個看起來合理但完全錯誤的日期。exactOptionalPropertyTypes 之下
  // `stats_from: undefined` 不是合法的值，所以用條件展開讓欄位整個不存在。
  const statsFrom = typeof o.stats_from === 'string' ? o.stats_from : undefined
  const statsTo = typeof o.stats_to === 'string' ? o.stats_to : undefined
  // 逐欄明寫而不是 `{ ...o }`：展開會把整包 unknown 帶進來、逼出一個 cast，而那個 cast 會連
  // 「SurveyContent 多了一個必填欄位、這裡沒建」都一起蓋掉（實測：加一個必填欄位，這個字面量
  // 報 TS2741，cast 版本零錯誤）。代價是後端多送的**問卷層**欄位不再被帶在 survey 物件上；
  // 題目那層仍原樣透傳，選填欄位（is_two_column / is_other 等）靠的是那一層。
  const survey: SurveyContent = {
    id: o.id,
    title: o.title,
    thumbnail: isAcceptedThumbnail(o.thumbnail, opts) ? o.thumbnail : '',
    description: typeof o.description === 'string' ? o.description : '',
    expired_at: typeof o.expired_at === 'string' ? o.expired_at : '',
    questions_per_page: typeof o.questions_per_page === 'number' ? o.questions_per_page : null,
    // 缺值或非 boolean（例如字串 "false"）一律視為顯示。
    does_show_logo: typeof o.does_show_logo === 'boolean' ? o.does_show_logo : true,
    questions,
    ...(statsFrom !== undefined ? { stats_from: statsFrom } : {}),
    ...(statsTo !== undefined ? { stats_to: statsTo } : {}),
  }
  return { survey, dropped }
}

// 一句總結而不是逐題一行：後端新增一種題型時，那十幾行講的是同一件事。
// required 單獨算是因為它是嚴重度的分野 —— 濾掉一題選填的，讀者少看到一題；濾掉一題必填的，
// 讀者根本沒被問到，送出的 payload 就少那一筆，後端收到一份「合法但不完整」的回應。
export function warnDroppedQuestions(surveyId: string, dropped: DroppedQuestions): void {
  if (dropped.total === 0)
    return
  console.warn(`[Pulse] survey ${surveyId}: ${dropped.total} question(s) failed validation and will not be rendered (${dropped.required} of them required).`)
}

// 整份被拒（top-level 結構壞掉）。這條路徑上沒有畫面可以掛提示，也沒有題數可以報 ——
// 沒有這句話的話，widget 就只是安靜地不見了。
export function warnMalformedSurvey(): void {
  console.warn('[Pulse] the survey payload could not be read (missing id / title / questions); nothing will be rendered.')
}

// 整份 result 讀不動（top-level 結構壞，見 parseResult）。正式路徑遇到同樣的東西是降級成致謝
// 畫面＋warn（見 submitSurvey），preview 跟著一致 —— 讀者看不到的東西，預覽也不該演出來。
// 只讀不動其中幾題的情形不走這裡，見 warnDroppedResultQuestions。
export function warnMalformedResult(): void {
  console.warn('[Pulse] the preview result payload could not be read; falling back to the thank-you screen.')
}

/** 一份結果裡讀不動、因而不會出現在結果頁的題目。 */
export interface DroppedResultQuestions {
  total: number
  // 其中拿得到數字 seq 的那幾題；seq 本身就壞掉的題只計入 total。
  seqs: number[]
}

export interface ParsedResult {
  result: SurveyResult
  dropped: DroppedResultQuestions
}

// 逐題的三種結局：'keep' 帶比例的選擇題，進結果頁；'skip' 契約允許、但結果頁不顯示的形狀，
// 不是錯誤；'bad' 讀不動，要被說出來。
type ResultQuestionVerdict = 'keep' | 'skip' | 'bad'

function isResultOptionShape(x: unknown): x is Record<string, unknown> {
  if (typeof x !== 'object' || x === null)
    return false
  const o = x as Record<string, unknown>
  return typeof o.content === 'string' && typeof o.is_selected === 'boolean'
}

// 判斷順序就是這幾條的先後，每一條只在前面都不成立時才輪到：
// 1. 題目本身的骨架（seq / text）不對 → bad。
// 2. 沒有 options（文字題只帶 answer、日期題只帶 date）或 options 是空陣列 → skip。
// 3. options 不是陣列，或有任何一個選項的 content / is_selected 型別不對 → bad。
// 4. 沒有任何選項帶 percent（個資題：契約只要求 content 與 is_selected）→ skip。
// 5. 每個選項的 percent 都是有限數 → keep。
// 6. 其餘（percent 非有限數、有的帶有的不帶）→ bad。混合的形狀不猜哪一半才對。
function classifyResultQuestion(x: unknown): ResultQuestionVerdict {
  if (typeof x !== 'object' || x === null)
    return 'bad'
  const q = x as Record<string, unknown>
  if (typeof q.seq !== 'number' || typeof q.text !== 'string')
    return 'bad'
  if (q.options === undefined)
    return 'skip'
  if (!Array.isArray(q.options))
    return 'bad'
  if (q.options.length === 0)
    return 'skip'
  const options: unknown[] = q.options
  if (!options.every(isResultOptionShape))
    return 'bad'
  if (options.every(o => o.percent === undefined))
    return 'skip'
  return options.every(o => Number.isFinite(o.percent)) ? 'keep' : 'bad'
}

// 結果頁只顯示「有比例的選擇題」，容錯逐題：壞掉的題只丟那一題，其他照樣顯示。送出（submitSurvey）
// 與 preview（survey-element 的 adoptPreviewResult）走同一支，所以是 export。
//
// 跟 parseSurvey 一樣是純函式、不 warn：preview 會隨 admin 的每個 keystroke 重跑，說話的時機屬於
// 呼叫端（見 warnDroppedResultQuestions）。只有 top-level 壞（id / stats_from / stats_to 不是字串、
// questions 不是陣列）才回 null；否則 result.questions 只含 keep 的題，被丟掉的記在 dropped。
// result 的其餘欄位原樣透傳，這裡只驗結果頁真正讀的那幾個。保留題數為 0 是否算「沒有結果」由
// 呼叫端決定（兩條路徑都當作沒有結果，走致謝畫面）。
export function parseResult(x: unknown): ParsedResult | null {
  if (typeof x !== 'object' || x === null)
    return null
  const o = x as Record<string, unknown>
  if (typeof o.id !== 'string' || typeof o.stats_from !== 'string' || typeof o.stats_to !== 'string' || !Array.isArray(o.questions))
    return null
  const kept: ResultQuestion[] = []
  const dropped: DroppedResultQuestions = { total: 0, seqs: [] }
  for (const q of o.questions) {
    const verdict = classifyResultQuestion(q)
    if (verdict === 'keep') {
      // TypeScript 不會驗證 classifyResultQuestion 的本體：'keep' 的保證由 api.test.ts 的
      // parseResult 逐格餵資料釘住。
      kept.push(q as ResultQuestion)
    }
    else if (verdict === 'bad') {
      dropped.total += 1
      const seq = (q as { seq?: unknown } | null)?.seq
      if (typeof seq === 'number')
        dropped.seqs.push(seq)
    }
  }
  return { result: { ...o, questions: kept } as unknown as SurveyResult, dropped }
}

// 一句總結而不是逐題一行；seq 讓人對得到是哪幾題。兩條路徑（submitSurvey、preview）共用這句話。
export function warnDroppedResultQuestions(surveyId: string, dropped: DroppedResultQuestions): void {
  if (dropped.total === 0)
    return
  const seqs = dropped.seqs.length > 0 ? ` (seq ${dropped.seqs.join(', ')})` : ''
  console.warn(`[Pulse] survey ${surveyId}: ${dropped.total} result question(s) could not be read and were left out of the results${seqs}.`)
}

export type GetSurveyResult
  = | { data: SurveyContent, error: undefined } // a survey to render
    | { data: undefined, error: undefined } // no survey to serve (204 / empty body / 404) — no-fill, not an error
    | { data: undefined, error: ApiError } // fetch / parse failure

// 送出失敗的三種說法。狀態碼在這個檔翻譯完，呼叫端只讀語意 —— errors.ts 據此挑文案，
// 不再自己認 400／409。無法辨識的失敗（5xx、網路中斷、沒有 status 的錯誤）一律 'unknown'，
// 它是明確的一員而不是「沒有 reason」，這樣文案表就只有一條 fallback 而不是兩條。
export type SubmitFailure = 'invalid' | 'duplicate' | 'unknown'

// 送出的結果，已翻成語意。`result` 為聚合統計（問卷顯示結果）或 undefined（不顯示結果，
// 送出仍成功 → 致謝）；'ended' 是問卷已結束（後端回 410），對呼叫端是換畫面而不是報錯。
export type SubmitOutcome
  = | { kind: 'submitted', result: SurveyResult | undefined }
    | { kind: 'ended' }
    | { kind: 'failed', reason: SubmitFailure, error: ApiError }

// GET /v1/surveys/{brand}?mp_id=&category_id= — category_id carries the CMS brand_category_id
// (the widget's categoryId option / category-id attribute is passed through under this key);
// mp_id is omitted for anonymous readers. A missing / non-numeric category_id yields 204 no-fill.
export async function getSurvey(brand: string, args: { mpId: string, category: string }, signal?: AbortSignal): Promise<GetSurveyResult> {
  const path = `/v1/surveys/${encodeURIComponent(brand)}`
  const res = await httpGet(resolveBaseUrl(), path, { query: { mp_id: args.mpId || undefined, category_id: args.category }, ...(signal ? { signal } : {}) })
  // 「沒有問卷可投」有兩種送達方式：204 No Content（空 body 的 2xx）與 404。兩者是同一件事，
  // 所以在這裡就併進無內容臂 —— 呼叫端讀「有沒有東西」，不必再認一次數字。與「有內容卻無法
  // 解析」的 malformed 分開：那個要落 error 臂。
  if (!res.ok)
    return res.error.status === 404 ? { data: undefined, error: undefined } : { data: undefined, error: res.error }
  if (res.data == null)
    return { data: undefined, error: undefined }
  const parsed = parseSurvey(res.data)
  if (!parsed) {
    warnMalformedSurvey()
    return { data: undefined, error: new ApiError('application', 'Malformed survey payload', { url: path }) }
  }
  // 一次 fetch 說一次。晚到的 mpid 會觸發第二次 load，那是真的第二次取得資料，再說一次是對的。
  warnDroppedQuestions(parsed.survey.id, parsed.dropped)
  return { data: parsed.survey, error: undefined }
}

// 失敗的狀態碼翻成語意。410 不走這裡 —— 它不是失敗，是問卷已結束（見 submitSurvey）。
function submitFailure(status: number | undefined): SubmitFailure {
  if (status === 400)
    return 'invalid'
  if (status === 409)
    return 'duplicate'
  return 'unknown'
}

// POST /v1/surveys/{brand} — 條件式回應：問卷「顯示結果」回聚合 SurveyResult，「不顯示結果」只回一個
// 建立成功的狀態（如 {"status":"created"}，無 id）。201 一律視為「票已進後端＝送出成功」：
// - 結果頁只收帶比例的選擇題，逐題讀（見 parseResult）：讀得動的題進 result，契約允許卻不顯示的
//   題（個資題、文字題、日期題、空 options）略過，讀不動的題丟掉並 console.warn 一次（帶 seq）；
// - 一題都不剩 → 送出成功、無結果 → 致謝；
// - 帶 id 卻整份讀不動（top-level 壞）→ 降級「無結果 → 致謝」＋ console.warn 供開發者 debug
//   （不對用戶報錯，避免票已收到卻誤導重投）；
// - 無 id（status-only）→ 送出成功、無結果 → 致謝。
export async function submitSurvey(brand: string, body: SurveySubmission): Promise<SubmitOutcome> {
  const path = `/v1/surveys/${encodeURIComponent(brand)}`
  const res = await httpPost(resolveBaseUrl(), path, body)
  if (!res.ok) {
    // 410 = 這份問卷已經結束，票不會再被收下。呼叫端據此換成結束畫面，所以它是一個具名的
    // 結果而不是一種錯誤 —— 沒有文案要挑，也不該走送出失敗那條路。
    if (res.error.status === 410)
      return { kind: 'ended' }
    return { kind: 'failed', reason: submitFailure(res.error.status), error: res.error }
  }
  const parsed = parseResult(res.data)
  if (!parsed) {
    if (res.data !== null && typeof res.data === 'object' && 'id' in res.data)
      console.warn(`[Pulse] submit succeeded but the result payload is malformed; falling back to the thank-you screen (${path}).`)
    return { kind: 'submitted', result: undefined }
  }
  warnDroppedResultQuestions(parsed.result.id, parsed.dropped)
  return { kind: 'submitted', result: parsed.result.questions.length > 0 ? parsed.result : undefined }
}
